import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Send as SendIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import {
  searchUsers,
  AdminUser,
  getPushTokens,
  sendTestNotification,
  getNotificationLogs,
  PushToken,
  NotificationLog,
} from '../adminApi';

// Pre-defined notification templates
const TEMPLATES = [
  {
    id: 'custom',
    label: 'Custom Message',
    title: '',
    body: '',
  },
  {
    id: 'plan_ready',
    label: 'Meal Plan Ready',
    title: 'Your meal plan is ready!',
    body: 'Tap to review and approve your personalized meal plan.',
  },
  {
    id: 'welcome',
    label: 'Welcome',
    title: 'Welcome to Snackd!',
    body: 'We\'re excited to help you plan delicious meals for your family.',
  },
  {
    id: 'issue_fixed',
    label: 'Issue Fixed',
    title: 'We fixed an issue',
    body: 'Thanks for reporting the problem. It\'s been resolved!',
  },
  {
    id: 'new_feature',
    label: 'New Feature',
    title: 'New feature available!',
    body: 'Check out the latest improvements we\'ve made to Snackd.',
  },
];

const PushNotifications: React.FC = () => {
  // User selection state
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Notification state
  const [selectedTemplate, setSelectedTemplate] = useState('custom');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ success: boolean; message: string } | null>(null);

  // Confirmation dialog
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Tokens and logs
  const [tokens, setTokens] = useState<PushToken[]>([]);
  const [logs, setLogs] = useState<NotificationLog[]>([]);
  const [loadingTokens, setLoadingTokens] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(true);

  // Search users with debounce
  const searchUsersDebounced = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setUsers([]);
      return;
    }

    setLoadingUsers(true);
    try {
      const result = await searchUsers({ query, limit: 10 });
      setUsers(result.users || []);
    } catch (err) {
      console.error('Failed to search users:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchUsersDebounced(userSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearchQuery, searchUsersDebounced]);

  // Load tokens and logs on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [tokensResult, logsResult] = await Promise.all([
          getPushTokens(),
          getNotificationLogs(),
        ]);
        setTokens(tokensResult.tokens);
        setLogs(logsResult.logs);
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setLoadingTokens(false);
        setLoadingLogs(false);
      }
    };
    loadData();
  }, []);

  // Handle template selection
  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = TEMPLATES.find(t => t.id === templateId);
    if (template && templateId !== 'custom') {
      setTitle(template.title);
      setBody(template.body);
    }
  };

  // Handle send
  const handleSendClick = () => {
    if (!selectedUser || !title || !body) return;
    setConfirmOpen(true);
  };

  const handleConfirmSend = async () => {
    setConfirmOpen(false);
    if (!selectedUser) return;

    setSending(true);
    setSendResult(null);

    try {
      const result = await sendTestNotification(selectedUser.user_id, title, body);
      setSendResult({ success: true, message: result.message });

      // Refresh logs
      const logsResult = await getNotificationLogs();
      setLogs(logsResult.logs);
    } catch (err: any) {
      setSendResult({ success: false, message: err.message || 'Failed to send notification' });
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const getUserHasToken = (userId: number) => {
    return tokens.some(t => t.user_id === userId && t.is_active);
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Push Notifications
      </Typography>

      {/* Send Notification Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SendIcon /> Send Notification
        </Typography>

        <Stack spacing={3}>
          {/* User Selection */}
          <Autocomplete
            options={users}
            getOptionLabel={(user) => `${user.email} (ID: ${user.user_id})`}
            value={selectedUser}
            onChange={(_, newValue) => setSelectedUser(newValue)}
            onInputChange={(_, newInput) => setUserSearchQuery(newInput)}
            loading={loadingUsers}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select User"
                placeholder="Search by email..."
                helperText={selectedUser && !getUserHasToken(selectedUser.user_id)
                  ? "Warning: This user has no active push tokens"
                  : undefined}
                FormHelperTextProps={{ sx: { color: 'warning.main' } }}
              />
            )}
            renderOption={(props, user) => (
              <li {...props} key={user.user_id}>
                <Stack direction="row" spacing={1} alignItems="center" width="100%">
                  <Typography>{user.email}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    (ID: {user.user_id})
                  </Typography>
                  {getUserHasToken(user.user_id) ? (
                    <Chip label="Has token" size="small" color="success" sx={{ ml: 'auto' }} />
                  ) : (
                    <Chip label="No token" size="small" color="default" sx={{ ml: 'auto' }} />
                  )}
                </Stack>
              </li>
            )}
          />

          {/* Template Selection */}
          <FormControl fullWidth>
            <InputLabel>Template</InputLabel>
            <Select
              value={selectedTemplate}
              label="Template"
              onChange={(e) => handleTemplateChange(e.target.value)}
            >
              {TEMPLATES.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Title & Body */}
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            fullWidth
            multiline
            rows={2}
            required
          />

          {/* Send Button */}
          <Box>
            <Button
              variant="contained"
              startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              onClick={handleSendClick}
              disabled={!selectedUser || !title || !body || sending}
            >
              {sending ? 'Sending...' : 'Send Notification'}
            </Button>
          </Box>

          {/* Result Message */}
          {sendResult && (
            <Alert severity={sendResult.success ? 'success' : 'error'}>
              {sendResult.message}
            </Alert>
          )}
        </Stack>
      </Paper>

      {/* Registered Tokens Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationsIcon /> Registered Tokens ({tokens.length})
        </Typography>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>User</TableCell>
                <TableCell>Platform</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Last Used</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingTokens ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : tokens.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    No registered tokens
                  </TableCell>
                </TableRow>
              ) : (
                tokens.map((token) => (
                  <TableRow key={token.token_id} hover>
                    <TableCell>
                      <Typography variant="body2">{token.email}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {token.user_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={token.platform}
                        size="small"
                        color={token.platform === 'ios' ? 'primary' : 'success'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={token.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={token.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{token.app_version || '-'}</TableCell>
                    <TableCell>{formatDate(token.last_used_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Recent Notifications Log */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Recent Notifications
        </Typography>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>User</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sent At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingLogs ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    No notifications sent yet
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.log_id} hover>
                    <TableCell>
                      <Typography variant="body2">{log.email || `User ${log.user_id}`}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={log.notification_type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {log.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.status}
                        size="small"
                        color={log.status === 'sent' ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>{formatDate(log.sent_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Send Push Notification?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to send a push notification to:
          </DialogContentText>
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2"><strong>User:</strong> {selectedUser?.email}</Typography>
            <Typography variant="body2"><strong>Title:</strong> {title}</Typography>
            <Typography variant="body2"><strong>Body:</strong> {body}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmSend} variant="contained" color="primary">
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PushNotifications;
